/**
 * Shared pub/sub module for Consistency cache invalidation.
 *
 * A2F-462 follow-up (commit 2/3): Dashboard, Plan, and Review pages all
 * consume the server-aggregated `/consistency` endpoint. Any of them can
 * trigger a scan (individual or batch), and when a scan completes the cached
 * consistency data for that project+submission is stale everywhere.
 *
 * Contract:
 *   - Emitters call `invalidateConsistency(projectId)` after a scan completes.
 *   - Subscribers pass a listener to `onConsistencyInvalidated` and receive the
 *     projectId every time any emitter fires. They decide whether to refetch
 *     (typically: only if the fired projectId matches their active project).
 *   - Subscribers MUST unsubscribe in their cleanup (the returned function) to
 *     avoid leaks when pages unmount.
 *
 * Intentionally NOT batched / debounced: if a batch scan fires N emits, we
 * accept N refetches. If that becomes a perf issue we add a coalescing layer;
 * debouncing prematurely risks masking legitimate "two independent scans done"
 * cases. No scope creep per the A2F-462 commit-2 rules.
 */

type Listener = (projectId: string) => void;

const listeners = new Set<Listener>();

/** Register a listener. Returns an unsubscribe function. */
export function onConsistencyInvalidated(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Fire all listeners. Safe to call from any scan-completion path. */
export function invalidateConsistency(projectId: string): void {
  // Snapshot before iterating — a listener that unsubscribes itself during
  // notification must not mutate the set we're iterating over.
  const snapshot = Array.from(listeners);
  for (const fn of snapshot) {
    try {
      fn(projectId);
    } catch (err) {
      // A misbehaving listener must not break other subscribers or the
      // emitter's scan-completion flow.
      // eslint-disable-next-line no-console
      console.error('consistencyInvalidation listener threw:', err);
    }
  }
}
